import omit from "lodash/omit";
import { Alternate, BLANK_ALTERNATE_LABEL } from "../../interfaces/Alternate";
import { SortField } from "../../interfaces/SortField";
import { CustomSummary, RowGroupType, OverlineQuantitiesMap } from "../../interfaces/OverlineTotals";
import { MFUFlookupByDepth, safeSortFieldColumnName } from "../utils/sortCodes";
import { fetchReportDetailSummaries } from "../../api";

function listsHaveSameElements<T>(a: T[], b: T[]): boolean {
  if (a.length !== b.length) return false; // different sizes ⇒ no match

  const setB = new Set(b);
  for (const x of a) {
    if (!setB.has(x)) return false; // missing element ⇒ no match
  }

  return true; // all found, sizes equal ⇒ match
}

export const createOverlineQuantitiesMap = async (
  estimateId: string,
  usedSortFields: { [key: string]: SortField },
  alternates: Alternate[],
  mfLookup: MFUFlookupByDepth,
  ufLookup: MFUFlookupByDepth,
  sortFieldColumnNameMap: Record<string, string> = {},
  labelSortFieldColumnNameMap: Record<string, string> = {}
): Promise<OverlineQuantitiesMap> => {
  const result: OverlineQuantitiesMap = {};

  try {
    const customSummaries = await getReportDetailSummaries(estimateId);
    if (!customSummaries) {
      return result;
    }

    for (const item of customSummaries) {
      const quantityValue = item.quantity.value;
      const uom = item.uom || "";

      let currentLevel = result;

      // `fields` is the id-keyed copy of this path, not a group level.
      for (const [_, group] of Object.entries(omit(item.grouping, "fields"))) {
        const groupValue = group.value || "";
        const groupType = group.type as RowGroupType;
        let sortCodeAndName = groupValue;
        let sortFieldName = groupType as string;

        if (groupType.startsWith("label_sort_field_")) {
          const sortId = groupType.slice(17);
          sortFieldName =
            labelSortFieldColumnNameMap[sortId] ||
            `label_${sortFieldColumnNameMap[sortId] || safeSortFieldColumnName(usedSortFields[sortId]?.name || sortFieldName)}`;
        } else if (groupType.startsWith("sort_field_")) {
          const sortId = groupType.slice(11);
          sortFieldName =
            sortFieldColumnNameMap[sortId] || safeSortFieldColumnName(usedSortFields[sortId]?.name || sortFieldName);
        } else if (groupType === "alternate") {
          const alternateKey = groupValue;
          const alternate = alternates.find((alt) => alt.key === alternateKey);
          sortCodeAndName = alternate ? `${alternate.key} - ${alternate.label}` : BLANK_ALTERNATE_LABEL;
        } else if (groupType === "estimate_use_groups") {
          sortFieldName = "use_group";
        } else if (groupType.slice(0, 2) === "mf" || groupType.slice(0, 2) === "uf") {
          const lookup = groupType.slice(0, 2) === "mf" ? mfLookup : ufLookup;
          // Create a flat lookup object: { code: "code - description" }
          const combinedLookup = Object.values(lookup).reduce(
            (acc, depthObj) => {
              Object.entries(depthObj).forEach(([code, desc]) => {
                acc[code] = `${code} - ${desc}`;
              });
              return acc;
            },
            {} as Record<string, string>
          );
          sortCodeAndName = combinedLookup[groupValue] || groupValue;
          //sortFieldName = sortCodeAndName.split(" - ")[0]
        }

        if (!(sortFieldName in currentLevel)) {
          currentLevel[sortFieldName] = {};
        }
        currentLevel = currentLevel[sortFieldName];

        if (!(sortCodeAndName in currentLevel)) {
          currentLevel[sortCodeAndName] = {};
        }
        currentLevel = currentLevel[sortCodeAndName];
      }

      // Ensure quantity and uom are stored at the correct level
      currentLevel["__quantity__"] = quantityValue;
      currentLevel["__uom__"] = uom;
    }

    return result;
  } catch (error) {
    console.error("Error creating overline quantities map", error);
    return result;
  }
};

export const lookupOverlineQuantity = (
  overlineQuantitiesMap: OverlineQuantitiesMap,
  lineage: { [key: string]: string }[]
): { quantity: number | null; uom: string | null } => {
  let lookup = overlineQuantitiesMap;

  for (const segment of lineage) {
    const groupByProp = Object.keys(segment)[0]; // Extracts key name (e.g., "mf1", "mf2")
    const searchKey = segment[groupByProp]; // Extracts the group value (e.g., "01 00 00")
    if (!lookup[groupByProp]) {
      return { quantity: null, uom: null };
    }

    if (groupByProp === "use_group") {
      if (lookup[groupByProp][searchKey]) {
        lookup = lookup[groupByProp][searchKey];
      } else {
        // this is needed bc of the arbitrary way use groups are concatenated in row groups
        for (const key of Object.keys(lookup[groupByProp])) {
          if (
            listsHaveSameElements(
              key.split(",").map((s) => s.trim()),
              searchKey.split(",").map((s) => s.trim())
            )
          ) {
            lookup = lookup[groupByProp][key];
          }
        }
      }
    } else if (groupByProp.slice(0, 2) === "mf" || groupByProp.slice(0, 2) === "uf") {
      if (lookup[groupByProp]) {
        // Search through all entries to find matching key
        let found = false;
        for (const [fullKey, value] of Object.entries(lookup[groupByProp])) {
          const code = fullKey.split(" - ")[0];
          if (code === searchKey && typeof value === "object" && value !== null) {
            lookup = value;
            found = true;
            break;
          }
        }
        if (!found) {
          return { quantity: null, uom: null };
        }
      } else {
        return { quantity: null, uom: null };
      }
    } else {
      if (lookup[groupByProp] && lookup[groupByProp][searchKey]) {
        lookup = lookup[groupByProp][searchKey]; // Move deeper into the correct property
      } else {
        return { quantity: null, uom: null }; // Stop if path is invalid
      }
    }
  }

  return {
    quantity: lookup?.["__quantity__"] ?? null,
    uom: lookup?.["__uom__"] ?? null,
  };
};

const getReportDetailSummaries = async (estimateId: string): Promise<CustomSummary[] | undefined> => {
  try {
    const summariesResult = await fetchReportDetailSummaries(estimateId);
    if (!summariesResult.success) {
      throw summariesResult.error;
    }
    return summariesResult.data;
  } catch (error) {
    console.error("Error fetching report detail summaries:", error);
    return undefined;
  }
};
